import {
  FormulaVariable,
  FormulaRouteResult,
  FormulaUnblockingSuggestion,
} from '../types';
import { FORMULA_VARIABLES, FORMULA_EQUATIONS } from '../data/formulas';
import {
  createStep,
  expandKnownAliases,
  findMinimalDeductionChains,
} from './formulaBfs';

export { createStep, expandKnownAliases, findMinimalDeductionChains };

/**
 * Deterministic Hyperedge BFS Graph Solver:
 * - Finds all minimal length deduction chains (surfacing ties rather than silently picking one)
 * - Returns rearranged LaTeX for every single step
 * - Connects official Anexo VI descritores tagged directly on formulas
 * - Explores 1-step breadth-first deducible alternatives from current knowns
 * - Pinpoints single unblocking grandezas if current data is insufficient (O(N) zero-recursion)
 */
export function findFormulaRoute(
  knownVarIds: string[],
  targetVarId: string,
  computeUnblocking = true
): FormulaRouteResult {
  const initialKnowns = new Set(knownVarIds);
  expandKnownAliases(initialKnowns);

  // If target is already known or alias of known
  if (initialKnowns.has(targetVarId)) {
    return {
      found: true,
      target: targetVarId,
      knowns: knownVarIds,
      chains: [],
      steps: [],
      otherReachable: [],
    };
  }

  // Compute other 1-step reachable grandezas from the same known set (breadth-first exploration)
  const otherReachable: { variable: FormulaVariable; route: FormulaRouteResult }[] = [];
  for (const v of FORMULA_VARIABLES) {
    if (v.id === targetVarId || initialKnowns.has(v.id)) continue;

    // Check if any equation solves for v in 1 step from initialKnowns
    for (const eq of FORMULA_EQUATIONS) {
      if (eq.variables.includes(v.id)) {
        const remaining = eq.variables.filter((rv) => rv !== v.id && !initialKnowns.has(rv));
        if (remaining.length === 0) {
          otherReachable.push({
            variable: v,
            route: {
              found: true,
              target: v.id,
              knowns: knownVarIds,
              chains: [
                {
                  id: `reachable-${eq.id}`,
                  steps: [createStep(eq, v.id)],
                },
              ],
              steps: [createStep(eq, v.id)],
            },
          });
          break; // found one direct route for this variable
        }
      }
    }
  }

  // Search minimal deduction chains (up to 4 steps)
  const chains = findMinimalDeductionChains(initialKnowns, targetVarId, 4);

  if (chains.length > 0) {
    return {
      found: true,
      target: targetVarId,
      knowns: knownVarIds,
      chains,
      steps: chains[0].steps,
      otherReachable,
    };
  }

  // 3. Unsolvable with current data: compute single unblocking grandezas deterministically
  const unblockingSuggestions: FormulaUnblockingSuggestion[] = [];

  if (computeUnblocking) {
    const seenVarIds = new Set<string>();

    // Pass 1: Direct 1-step unblockers (equations with targetVarId missing exactly 1 variable)
    for (const eq of FORMULA_EQUATIONS) {
      if (!eq.variables.includes(targetVarId)) continue;
      const missing = eq.variables.filter((v) => v !== targetVarId && !initialKnowns.has(v));
      if (missing.length === 1) {
        const missingVarId = missing[0];
        if (!seenVarIds.has(missingVarId)) {
          seenVarIds.add(missingVarId);
          const varObj = FORMULA_VARIABLES.find((v) => v.id === missingVarId);
          if (varObj) {
            unblockingSuggestions.push({
              variable: varObj,
              stepsNeeded: 1,
              unblocksVia: eq.name,
            });
          }
        }
      }
    }

    // Pass 2: If fewer than 6 suggestions, check candidate variables connected to target or knowns (max 2 steps)
    if (unblockingSuggestions.length < 6) {
      const relevantVarIds = new Set<string>();
      for (const eq of FORMULA_EQUATIONS) {
        if (eq.variables.includes(targetVarId) || eq.variables.some((v) => initialKnowns.has(v))) {
          for (const v of eq.variables) {
            if (v !== targetVarId && !initialKnowns.has(v) && !seenVarIds.has(v)) {
              relevantVarIds.add(v);
            }
          }
        }
      }

      for (const candId of relevantVarIds) {
        const candVar = FORMULA_VARIABLES.find((v) => v.id === candId);
        if (!candVar) continue;

        const augmentedKnowns = new Set(initialKnowns);
        augmentedKnowns.add(candId);
        expandKnownAliases(augmentedKnowns);

        const testChains = findMinimalDeductionChains(augmentedKnowns, targetVarId, 2);
        if (testChains.length > 0) {
          seenVarIds.add(candId);
          unblockingSuggestions.push({
            variable: candVar,
            stepsNeeded: testChains[0].steps.length,
            unblocksVia: testChains[0].steps[0].equation.name,
          });
          if (unblockingSuggestions.length >= 6) break;
        }
      }
    }

    // Sort by shortest resolution chain length
    unblockingSuggestions.sort((a, b) => a.stepsNeeded - b.stepsNeeded);
  }

  return {
    found: false,
    target: targetVarId,
    knowns: knownVarIds,
    chains: [],
    steps: [],
    otherReachable,
    unblockingGrandezas: unblockingSuggestions.slice(0, 6),
  };
}
